(() => {
  const STORAGE_KEY = 'thb_recent_searches_v2';
  const MAX = 10;

  const WRAP_SELECTOR = '.thb-predictive-search';
  const LIST_SELECTOR = '[data-recent-list]';

  // ---------- storage ----------
  const getStore = () => {
    try {
      const stored = JSON.parse(
        localStorage.getItem(STORAGE_KEY) || '[]'
      );

      return Array.isArray(stored)
        ? stored
            .map((term) => normalize(term))
            .filter(Boolean)
            .slice(0, MAX)
        : [];
    } catch (error) {
      return [];
    }
  };

  const setStore = (terms) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(terms.slice(0, MAX))
      );
    } catch (error) {
      console.warn(
        'Unable to save recent searches:',
        error
      );
    }
  };

  const normalize = (raw) =>
    (raw || '').toString().trim();

  function addTerm(raw, options = {}) {
    const term = normalize(raw);

    if (!term) return;

    const terms = getStore().filter(
      (storedTerm) =>
        storedTerm.toLowerCase() !== term.toLowerCase()
    );

    terms.unshift(term);
    setStore(terms);

    // Do not rerender while a clicked link is navigating.
    if (options.render !== false) {
      scheduleRender();
    }
  }

  function removeTerm(raw) {
    const term = normalize(raw);

    if (!term) return;

    const terms = getStore().filter(
      (storedTerm) =>
        storedTerm.toLowerCase() !== term.toLowerCase()
    );

    setStore(terms);
    scheduleRender();
  }

  function clearAll() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn(
        'Unable to clear recent searches:',
        error
      );
    }

    scheduleRender();
  }

  // ---------- render checks ----------
  function listMatchesStore(list, stored) {
    const currentRows = Array.from(
      list.querySelectorAll(
        ':scope > .thb-ps-recent-row'
      )
    );

    const emptyNote = list.querySelector(
      ':scope > [data-recent-empty]'
    );

    if (!stored.length) {
      return (
        currentRows.length === 0 &&
        Boolean(emptyNote)
      );
    }

    if (
      emptyNote ||
      currentRows.length !== stored.length
    ) {
      return false;
    }

    return currentRows.every((row, index) => {
      const link = row.querySelector(
        '.predictive-search__item'
      );

      const currentTerm = normalize(
        link?.dataset.label ||
        link?.textContent ||
        ''
      );

      return currentTerm === stored[index];
    });
  }

  // ---------- render ----------
  function renderIntoWrap(wrap) {
    const lists = wrap.querySelectorAll(LIST_SELECTOR);

    if (!lists.length) return;

    const stored = getStore();

    lists.forEach((list) => {
      const recentGroup = list.closest(
        '.thb-ps__group--recent'
      );

      const clearAllButton =
        recentGroup?.querySelector(
          '[data-recent-clear-all]'
        ) ||
        wrap.querySelector(
          '[data-recent-clear-all]'
        );

      const alreadyCorrect =
        listMatchesStore(list, stored);

      if (clearAllButton) {
        clearAllButton.hidden =
          stored.length === 0;
      }

      if (alreadyCorrect) return;

      const fragment =
        document.createDocumentFragment();

      if (!stored.length) {
        const emptyItem =
          document.createElement('li');

        emptyItem.className =
          'thb-ps__empty-note';

        emptyItem.dataset.recentEmpty = '';
        emptyItem.textContent =
          'No recent searches yet.';

        fragment.appendChild(emptyItem);
      } else {
        stored.forEach((term) => {
          const row =
            document.createElement('li');

          row.className =
            'thb-ps-recent-row';

          const link =
            document.createElement('a');

          link.className =
            'predictive-search__item link link--text';

          link.href =
            `/search?q=${encodeURIComponent(term)}`;

          link.dataset.label = term;
          link.textContent = term;

          const removeButton =
            document.createElement('button');

          removeButton.type = 'button';
          removeButton.className =
            'thb-ps-recent-remove';

          removeButton.setAttribute(
            'aria-label',
            `Remove '${term}'`
          );

          removeButton.dataset.recentRemove =
            term;

          removeButton.textContent = '✕';

          row.appendChild(link);
          row.appendChild(removeButton);
          fragment.appendChild(row);
        });
      }

      list.replaceChildren(fragment);
    });
  }

  function renderAll() {
    const stored = getStore();

    document
      .querySelectorAll(LIST_SELECTOR)
      .forEach((list) => {
        const wrap =
          list.closest(WRAP_SELECTOR);

        if (!wrap) return;

        const recentGroup = list.closest(
          '.thb-ps__group--recent'
        );

        const clearAllButton =
          recentGroup?.querySelector(
            '[data-recent-clear-all]'
          );

        if (clearAllButton) {
          clearAllButton.hidden =
            stored.length === 0;
        }

        if (listMatchesStore(list, stored)) {
          return;
        }

        const fragment =
          document.createDocumentFragment();

        if (!stored.length) {
          const emptyItem =
            document.createElement('li');

          emptyItem.className =
            'thb-ps__empty-note';

          emptyItem.dataset.recentEmpty = '';
          emptyItem.textContent =
            'No recent searches yet.';

          fragment.appendChild(emptyItem);
        } else {
          stored.forEach((term) => {
            const row =
              document.createElement('li');

            row.className =
              'thb-ps-recent-row';

            const link =
              document.createElement('a');

            link.className =
              'predictive-search__item link link--text';

            link.href =
              `/search?q=${encodeURIComponent(term)}`;

            link.dataset.label = term;
            link.textContent = term;

            const removeButton =
              document.createElement('button');

            removeButton.type = 'button';
            removeButton.className =
              'thb-ps-recent-remove';

            removeButton.setAttribute(
              'aria-label',
              `Remove '${term}'`
            );

            removeButton.dataset.recentRemove =
              term;

            removeButton.textContent = '✕';

            row.appendChild(link);
            row.appendChild(removeButton);
            fragment.appendChild(row);
          });
        }

        list.replaceChildren(fragment);
      });
  }

  // ---------- render scheduling ----------
  let rafId = null;

  function scheduleRender() {
    if (rafId !== null) return;

    rafId = requestAnimationFrame(() => {
      rafId = null;
      renderAll();
    });
  }


  // ---------- search input helpers ----------
  function isSearchInput(element) {
    return Boolean(
      element?.matches?.(
        'input[name="q"], input[type="search"]'
      )
    );
  }

  function getInputValueFromEventTarget(target) {
    if (target?.querySelector) {
      const input = target.querySelector(
        'input[name="q"], input[type="search"]'
      );

      if (input) return input.value;
    }

    const form = target?.closest?.('form');

    const input = form?.querySelector?.(
      'input[name="q"], input[type="search"]'
    );

    return input?.value || '';
  }

  // ---------- save submitted searches ----------
  document.addEventListener(
    'submit',
    (event) => {
      addTerm(
        getInputValueFromEventTarget(
          event.target
        )
      );
    },
    true
  );

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Enter') return;
      if (!isSearchInput(event.target)) return;

      addTerm(event.target.value);
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const submitButton =
        event.target.closest(
          'button[type="submit"], .thb-search-submit'
        );

      if (!submitButton) return;

      addTerm(
        getInputValueFromEventTarget(
          submitButton
        )
      );
    },
    true
  );

  // ---------- recent-search interactions ----------
  document.addEventListener(
    'click',
    (event) => {
      const removeButton =
        event.target.closest(
          '[data-recent-remove]'
        );

      if (removeButton) {
        event.preventDefault();
        event.stopImmediatePropagation();

        removeTerm(
          removeButton.getAttribute(
            'data-recent-remove'
          )
        );

        return;
      }

      const clearButton =
        event.target.closest(
          '[data-recent-clear-all]'
        );

      if (clearButton) {
        event.preventDefault();
        event.stopImmediatePropagation();

        clearAll();
        return;
      }

      const predictiveLink =
        event.target.closest(
          '.predictive-search__item'
        );

      if (!predictiveLink) return;

      const label =
        predictiveLink.getAttribute(
          'data-label'
        ) ||
        predictiveLink.textContent;

      // Save without rebuilding the DOM during navigation.
      addTerm(label, {
        render: false
      });
    },
    true
  );

  // ---------- empty input ----------
  document.addEventListener(
    'input',
    (event) => {
      if (!isSearchInput(event.target)) return;

      const query = normalize(
        event.target.value
      );

      if (!query) {
        forceRecentSearchRender();
        runStartupRenderWatch();
      }
    },
    true
  );

 
  // ---------- search opening ----------
  document.addEventListener(
    'focusin',
    (event) => {
      if (!isSearchInput(event.target)) return;

      forceRecentSearchRender();
      runStartupRenderWatch();
    },
    true
  );

   // ---------- predictive markup replacement ----------
  let startupRenderTimer = null;

  function forceRecentSearchRender() {
    renderAll();

    requestAnimationFrame(() => {
      renderAll();
    });

    setTimeout(renderAll, 50);
    setTimeout(renderAll, 150);
    setTimeout(renderAll, 350);
    setTimeout(renderAll, 750);
  }

  const recentSearchObserver =
    new MutationObserver((mutations) => {
      const shouldRestoreRecentSearches =
        mutations.some((mutation) => {
          const target =
            mutation.target instanceof Element
              ? mutation.target
              : mutation.target.parentElement;

          /*
           * Detect changes directly inside an existing recent-search list.
           */
          if (
            target?.matches?.(LIST_SELECTOR) ||
            target?.closest?.(LIST_SELECTOR)
          ) {
            return true;
          }

          /*
           * Detect a newly inserted predictive wrapper or recent-search list.
           */
          return Array.from(
            mutation.addedNodes
          ).some((node) => {
            if (!(node instanceof Element)) {
              return false;
            }

            return (
              node.matches?.(WRAP_SELECTOR) ||
              node.querySelector?.(WRAP_SELECTOR) ||
              node.matches?.(LIST_SELECTOR) ||
              node.querySelector?.(LIST_SELECTOR)
            );
          });
        });

      if (!shouldRestoreRecentSearches) return;

      /*
       * Render synchronously first. The delayed renders handle any
       * additional Vision/theme replacements that follow.
       */
      forceRecentSearchRender();
    });

  recentSearchObserver.observe(
    document.body,
    {
      childList: true,
      subtree: true
    }
  );

  // ---------- initial page-load protection ----------
  function runStartupRenderWatch() {
    clearInterval(startupRenderTimer);

    const startedAt = Date.now();

    /*
     * Briefly keep the recent-search DOM synchronized while the
     * theme finishes initializing the search component.
     */
    startupRenderTimer = setInterval(() => {
      renderAll();

      if (Date.now() - startedAt >= 4000) {
        clearInterval(startupRenderTimer);
        startupRenderTimer = null;
      }
    }, 100);
  }

  // Render immediately.
  forceRecentSearchRender();
  runStartupRenderWatch();

  // Render again at normal document milestones.
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      forceRecentSearchRender();
      runStartupRenderWatch();
    },
    { once: true }
  );

  window.addEventListener(
    'load',
    () => {
      forceRecentSearchRender();
      runStartupRenderWatch();
    },
    { once: true }
  );
})();
